import { faArrowLeft, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormattedMessage } from "react-intl";
import Sheet from "../Sheet";

/**
 * The bottom sheet every filter uses. Sized to its content, never to the screen.
 *
 * Every sheet ends the same way: Clear on the left, Apply on the right, and an x in
 * the header to leave without changing anything. There is no Reset, and no result
 * count on the button — that number is not known until the query runs.
 */
function FilterSheet({
  title,
  subtitle,
  onBack,
  onClose,
  onClear,
  onApply,
  children,
  testId,
  clearLabel,
  applyLabel,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Present on a sublist: returns to More filters. */
  onBack?: () => void;
  /** The x, and the overlay: leaves without changing anything. */
  onClose: () => void;
  onClear: () => void;
  onApply: () => void;
  children: React.ReactNode;
  testId?: string;
  /** The Place sheet's foot reads the pick back ("Everywhere" / "Show France"). */
  clearLabel?: React.ReactNode;
  applyLabel?: React.ReactNode;
}) {
  return (
    <Sheet onClose={onClose} testId={testId}>

        <div className="flex items-center gap-3 flex-none">
          {onBack && (
            <button type="button" onClick={onBack} className="text-muted" data-testid="filter-sheet-back">
              <FontAwesomeIcon icon={faArrowLeft} />
              <span className="sr-only"><FormattedMessage id="filter__sheet_back" /></span>
            </button>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sheet-title">{title}</div>
            {subtitle && <div className="text-meta text-muted mt-0.5">{subtitle}</div>}
          </div>
          <button type="button" onClick={onClose} className="text-muted" data-testid="filter-sheet-close">
            <FontAwesomeIcon icon={faXmark} />
            <span className="sr-only"><FormattedMessage id="filter__sheet_close" /></span>
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto -mx-sheet-padding-x px-sheet-padding-x">{children}</div>

        <div className="mt-4 flex items-center gap-2.5 flex-none">
          <button
            type="button"
            onClick={onClear}
            className="flex-none px-[22px] py-[15px] rounded-pill bg-tint-chip text-muted text-row"
            data-testid="filter-sheet-clear"
          >
            {clearLabel ?? <FormattedMessage id="filter__sheet_clear" />}
          </button>
          <button
            type="button"
            onClick={onApply}
            className="flex-1 flex items-center justify-center py-[15px] rounded-pill bg-tint-apply-bg text-tint-apply-fg text-body font-medium"
            data-testid="filter-sheet-apply"
          >
            {applyLabel ?? <FormattedMessage id="filter__sheet_apply" />}
          </button>
        </div>
    </Sheet>
  );
}

export default FilterSheet;
